import React, { MouseEventHandler } from 'react';
import sanitizeHtml, { IOptions } from 'sanitize-html';
import { useTheme } from 'styled-components';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import { DefaultStyledRichTextWrapper, RichTextColor } from './styles';
import useCustomBullets from './useCustomBullets';
import useOpenInNewTabIcons from './useOpenInNewTabIcons';

const sanitizeHtmlOptions: IOptions = {
  allowedTags: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'p',
    'a',
    'ul',
    'ol',
    'li',
    'b',
    'strong',
    'em',
    'br',
    'button',
    'table',
    'tr',
    'th',
    'td',
    'thead',
    'tbody',
    'span',
  ],
  allowedSchemes: ['http', 'https', 'tel', 'mailto'],
  allowedStyles: {
    '*': {
      'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
      'margin-top': [/^\d+(?:px|em)$/],
      'margin-bottom': [/^\d+(?:px|em)$/],
    },
  },
  allowedAttributes: {
    '*': ['class', 'style', 'data-*'],
    a: ['href', 'name', 'target', 'aria-label'],
    button: ['type', 'aria-label', 'aria-haspopup'],
  },
  transformTags: {
    button: (_, attributes) => ({
      tagName: 'button',
      attribs: {
        type: 'button',
        'aria-haspopup': 'dialog',
        ...attributes,
      },
    }),
  },
};

export type RichTextOnLinkClick = (label: string, url: string) => void;

export type RichTextProps = {
  html: string;
  customBulletIcon?: string;
  backgroundColor?: RichTextColor;
  linkBorderColor?: RichTextColor;
  onLinkClick?: RichTextOnLinkClick;
  newTabIconForExternalLinksOnly?: boolean;
  focusable?: boolean;
} & ComponentProps;

const RichText: React.FC<RichTextProps> = ({
  html,
  customBulletIcon,
  backgroundColor,
  linkBorderColor,
  onLinkClick,
  newTabIconForExternalLinksOnly,
  focusable,
  ...props
}) => {
  const { richTextOpenInNewTabIcon, externalOnlyRichTextOpenInNewTabIcon } = useTheme();
  const ref = React.useRef<HTMLDivElement>(null);
  const customBulletsPortals = useCustomBullets(ref, html, customBulletIcon);
  const openInNewTabIconsPortals = useOpenInNewTabIcons(
    ref,
    html,
    richTextOpenInNewTabIcon,
    externalOnlyRichTextOpenInNewTabIcon
  );
  const isFocusable = focusable ? { tabIndex: 0 } : undefined;

  const clickHandler: MouseEventHandler = (e) => {
    const link = (e.target as Element)?.closest('a');
    if (link && onLinkClick) {
      onLinkClick(link.innerText, link.href);
    }
  };

  return (
    <>
      <DefaultStyledRichTextWrapper
        useCustomBulletIcon={!!customBulletIcon}
        useOpenInNewTabIcons={!!richTextOpenInNewTabIcon}
        useExternalOpenInNewTabIcons={!!externalOnlyRichTextOpenInNewTabIcon}
        backgroundColor={backgroundColor}
        linkBorderColor={linkBorderColor}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html, sanitizeHtmlOptions) }}
        ref={ref}
        onClick={onLinkClick && clickHandler}
        {...{ ...componentProps(props), ...isFocusable }}
      />
      {customBulletsPortals}
      {openInNewTabIconsPortals}
    </>
  );
};

export default applyComponentTheme(RichText, 'RichText');
